/**
 * @author Hideo Yamada
 * @description https://xd.adobe.com/view/9088a00e-a17f-43e7-5a33-cfa5e833aac8-e6b9/screen/e264e544-0557-40d2-b820-21660b2afa70/Buttons
 */

import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { toast } from 'react-toastify';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { VARIANT_TYPE } from '~/services/constants';

const StyledButton = styled(Button)({
    padding: '8px 16px',
    borderRadius: 4,
    textTransform: 'initial',
    '&:disabled': {
        color: '#969faa',
        backgroundColor: '#efefef',
        borderColor: '#efefef',
    },
    '&.MuiButton-contained': {
        color: '#fff',
        backgroundColor: '#376caf',
        '&:hover': { backgroundColor: '#5381ba' },
        '&:focus': { backgroundColor: '#5381ba' },
        '&:active': { backgroundColor: '#376caf' },
    },
    '&.MuiButton-outlined': {
        color: '#376caf',
        borderColor: '#376caf',
        '&:hover': { backgroundColor: '#e6edf5', borderColor: '#4375b4', color: '#4375b4' },
        '&:focus': { borderColor: '#5381ba', color: '#5381ba' },
        '&:active': { borderColor: '#376caf', color: '#376caf' },
    },
    '&.MuiButton-text': {
        color: '#376caf',
        '&:hover': { backgroundColor: '#eff4f9', color: '#5f89ba' },
        '&:active': { color: '#5f89ba' },
    },
});

const AnimatedButton = ({ className, type = 'high', name, children, size = 'small', disabled = false, busy = false, ringSize = 18, alert, onClick }) => {
    const handleClick = (e) => {
        if (disabled && !alert) {
            return;
        } else if (disabled && !!alert) {
            toast.warn(alert, { position: toast.POSITION.TOP_RIGHT });
        } else {
            onClick && onClick(e);
        }
    };

    return (
        <StyledButton
            className={cx('core-animated-button', type, `size-${size}`, className, { disabled })}
            variant={VARIANT_TYPE[type] || 'contained'}
            size={size}
            color="primary"
            disabled={disabled && !alert}
            disableRipple={type === 'link'}
            onClick={handleClick}
        >
            {busy ? (
                <div className={`lds-dual-ring ring-${ringSize}`} />
            ) : children !== undefined && children !== null && children !== '' ? (
                children
            ) : (
                <div className={cx('truncate-one', type === 'high' || type === 'medium' ? 'text-400' : 'text-300')}>{name}</div>
            )}
        </StyledButton>
    );
};

AnimatedButton.propTypes = {
    className: PropTypes.string,
    type: PropTypes.oneOf(['high', 'medium', 'low', 'link']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    name: PropTypes.string,
    busy: PropTypes.bool,
    disabled: PropTypes.bool,
    /**
     * If button is disabled and it does have alert message, when clicking the button,
     * button will show a toast message with alert message.
     */
    alert: PropTypes.string,
    ringSize: PropTypes.number,
    onClick: PropTypes.func,
};

export default AnimatedButton;
